import { Injectable, inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { TenantService } from './tenant.service';

/** Platform defaults used when no tenant branding is available */
const PLATFORM_DEFAULTS = {
  name: 'School ERP',
  description: 'Smart school management platform for admissions, attendance, communication, and daily reports.',
  primaryColor: '#1A237E',
};

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private tenantService = inject(TenantService);

  /**
   * Initialize SEO meta tags from tenant config.
   * Called once during app initialization.
   */
  initialize(): void {
    const tenant = this.tenantService.getCurrentTenant();
    const branding = tenant?.branding;
    const brandName = branding?.name || tenant?.tenantName || PLATFORM_DEFAULTS.name;
    const description = branding?.description || PLATFORM_DEFAULTS.description;
    const logo = branding?.logo || '';
    const website = branding?.website || (typeof window !== 'undefined' ? window.location.origin : '');
    const primaryColor =
      tenant?.theme?.mainColors?.primaryColor ||
      branding?.primaryColor ||
      PLATFORM_DEFAULTS.primaryColor;

    // Page title
    this.titleService.setTitle(`${brandName} - Smart School Management`);

    // Standard meta
    this.metaService.updateTag({ name: 'description', content: description });
    this.metaService.updateTag({ name: 'author', content: brandName });
    this.metaService.updateTag({ name: 'theme-color', content: primaryColor });

    // Open Graph
    this.metaService.updateTag({ property: 'og:title', content: `${brandName} - Smart School Management` });
    this.metaService.updateTag({ property: 'og:description', content: description });
    this.metaService.updateTag({ property: 'og:site_name', content: brandName });
    this.metaService.updateTag({ property: 'og:url', content: website });
    if (logo) {
      this.metaService.updateTag({ property: 'og:image', content: logo });
    }

    // Twitter Card
    this.metaService.updateTag({ name: 'twitter:title', content: `${brandName} - Smart School Management` });
    this.metaService.updateTag({ name: 'twitter:description', content: description });
    if (logo) {
      this.metaService.updateTag({ name: 'twitter:image', content: logo });
    }

    // Canonical URL
    this.updateCanonical(website);
  }

  /**
   * Set a page-specific title while keeping the brand suffix.
   */
  setPageTitle(pageTitle: string): void {
    const brandName = this.tenantService.getBrandName();
    this.titleService.setTitle(pageTitle ? `${pageTitle} | ${brandName}` : brandName);
  }

  /**
   * Update the canonical link element.
   */
  private updateCanonical(url: string): void {
    if (typeof document === 'undefined' || !url) return;
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (link) {
      link.setAttribute('href', url);
    }
  }
}
